import { useComments } from "../../hooks/useComments";
import { useUser } from "../../context/UserContext";
import CommentItem from "./CommentItem";
import CommentInput from "./CommentInput";
import styles from "./CommentSection.module.scss";

interface CommentSectionProps {
  postId: string;
  onCommentCountChange?: (delta: number) => void;
}

export default function CommentSection({
  postId,
  onCommentCountChange,
}: CommentSectionProps) {
  const { comments, loading, error, hasMore, loadMore, addComment, removeComment, toggleCommentLike } =
    useComments(postId);
  const { user } = useUser();

  const handleAddComment = async (body: string) => {
    await addComment(body);
    onCommentCountChange?.(1);
  };

  const handleReply = async (parentCommentId: string, body: string) => {
    await addComment(body, parentCommentId);
    onCommentCountChange?.(1);
  };

  const handleDelete = async (commentId: string) => {
    await removeComment(commentId);
    onCommentCountChange?.(-1);
  };

  return (
    <div className={styles.commentSection}>
      {loading && comments.length === 0 && (
        <div className={styles.loading}>Loading comments...</div>
      )}
      {error && <div className={styles.error}>{error}</div>}

      {comments.map((comment) => (
        <CommentItem
          key={comment.commentId}
          comment={comment}
          onReply={handleReply}
          onDelete={handleDelete}
          onLike={toggleCommentLike}
          currentUserId={user?.userId ?? ""}
          currentUser={
            user
              ? { username: user.username, avatarUrl: user.avatarUrl, profileImageUrl: user.profileImageUrl }
              : undefined
          }
        />
      ))}

      {hasMore && (
        <button className={styles.viewMore} onClick={loadMore}>
          View more comments
        </button>
      )}

      {user && (
        <CommentInput
          onSubmit={handleAddComment}
          placeholder="Write a comment..."
        />
      )}
    </div>
  );
}
